/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { FiX } from 'react-icons/fi'

import { ACTION_META, ENTITY_META, TIME_PRESET_LABELS } from './constants'
import { formatCount, formatRangeLabel } from './format'
import type { AuditFacets, AuditFilters } from './types'

export interface FilterChip {
  key: string
  label: string
  /** The entity-history chip is a mode, not an ordinary filter, so it reads more prominently. */
  prominent?: boolean
  onRemove: () => void
}

interface AuditSummaryProps {
  filters: AuditFilters
  facets?: AuditFacets
  isLoading: boolean
  companyNames: Map<number, string>
  entityName?: string
  onChange: (patch: Partial<AuditFilters>) => void
}

/**
 * The result context line: the true total, the active filters restated in prose, and one-click
 * removal for each.
 *
 * The total is the server-side count for the filter set, never derived from loaded rows — a number
 * that grows as you scroll is worse than no number. While it loads, a skeleton stands in rather
 * than a wrong value.
 */
export const AuditSummary: React.FC<AuditSummaryProps> = ({
  filters,
  facets,
  isLoading,
  companyNames,
  entityName,
  onChange,
}) => {
  const chips: FilterChip[] = []

  if (filters.entityId) {
    chips.push({
      key: 'entity',
      label: `Showing all changes to “${entityName ?? filters.entityId}”`,
      prominent: true,
      onRemove: () => onChange({ entityId: undefined }),
    })
  }

  filters.changeTypes.forEach((value) =>
    chips.push({
      key: `action-${value}`,
      label: ACTION_META[value].label,
      onRemove: () => onChange({ changeTypes: filters.changeTypes.filter((v) => v !== value) }),
    })
  )

  filters.entityTypes.forEach((value) =>
    chips.push({
      key: `entity-${value}`,
      label: ENTITY_META[value].label,
      onRemove: () => onChange({ entityTypes: filters.entityTypes.filter((v) => v !== value) }),
    })
  )

  filters.userIds.forEach((value) => {
    const name = facets?.users.find((u) => u.userId === value)?.userName
    chips.push({
      key: `user-${value}`,
      label: name ?? `User #${value}`,
      onRemove: () => onChange({ userIds: filters.userIds.filter((v) => v !== value) }),
    })
  })

  if (filters.search.trim()) {
    chips.push({
      key: 'search',
      label: `“${filters.search.trim()}”`,
      onRemove: () => onChange({ search: '' }),
    })
  }

  const rangeLabel =
    filters.time === 'custom'
      ? formatRangeLabel(filters.from, filters.to) || 'Custom range'
      : TIME_PRESET_LABELS[filters.time]

  const companyLabel =
    filters.companyIds.length === 0
      ? 'No company selected'
      : filters.companyIds.length === 1
        ? (companyNames.get(filters.companyIds[0]) ?? `Company #${String(filters.companyIds[0])}`)
        : `${String(filters.companyIds.length)} companies`

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-base-content/70">
      {isLoading ? (
        <span className="inline-block h-4 w-24 animate-pulse rounded bg-base-300" aria-label="Loading total" />
      ) : (
        <span className="font-medium text-base-content">{formatCount(facets?.total ?? 0)} changes</span>
      )}
      <span aria-hidden>·</span>
      <span>{rangeLabel}</span>
      <span aria-hidden>·</span>
      <span>{companyLabel}</span>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={chip.onRemove}
              aria-label={`Remove filter ${chip.label}`}
              className={`inline-flex max-w-[280px] items-center gap-1 rounded-selector px-2 py-0.5 text-xs ${
                chip.prominent
                  ? 'bg-primary text-primary-content hover:bg-primary/85'
                  : 'bg-base-300 text-base-content hover:bg-base-300/70'
              }`}
            >
              <span className="truncate">{chip.label}</span>
              <FiX aria-hidden className="shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
